import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { GeneralPricingMode, Service } from '@mews-commander/data-access';
import { useFormControls } from '@mews/framework';
import { SubmitHandlers, useProductServiceOrdersAdd } from '../../../queries/useProductServiceOrdersAdd';
import { createResolver, ProductOrderFormValues, ServiceOrderFormField } from '../AddProduct/schema';
import { useServiceCurrency } from './useServiceCurrency';
import { useCalculateTaxValues } from './useCalculateTaxValues';
import { useDefaultFormValues } from './useDefaultFormValues';

export const useAddProductForm = ({
    service,
    billId,
    accountId,
    scopedReservationIds,
    ...submitHandlers
}: {
    service: Service;
    billId?: string;
    scopedReservationIds?: string[];
    accountId: string;
} & SubmitHandlers) => {
    const { mutate, isPending } = useProductServiceOrdersAdd(submitHandlers);

    const { currency } = useServiceCurrency(service);

    const { defaultStartUtc, defaultAmount, defaultTaxValues, defaultLinkedReservationId } = useDefaultFormValues(
        service,
        accountId,
        scopedReservationIds
    );

    const hasEnterpriseNetPricing = useCurrentEnterprise().pricing === GeneralPricingMode.Net;

    const { hasOverridablePrice } = service.options;

    const calculateTaxValues = useCalculateTaxValues();

    const { renderFormContent, submit, watch, reset, hasDirtyFields, validate } = useFormControls({
        resolver: createResolver({
            notesRequired: service.options.orderRequiresCompletedNotes,
        }),
        defaultValues: {
            [ServiceOrderFormField.Value]: defaultAmount,
            [ServiceOrderFormField.StartUtc]: defaultStartUtc,
            [ServiceOrderFormField.SegmentId]: service.defaultBusinessSegmentId || '',
            [ServiceOrderFormField.TaxValues]: defaultTaxValues,
            [ServiceOrderFormField.Currency]: currency,
            [ServiceOrderFormField.LinkedReservationId]: defaultLinkedReservationId,
        },
        onSubmit: (values) => {
            mutate({
                productServiceOrders: [
                    {
                        serviceId: service.id,
                        accountId,
                        billId,
                        linkedReservationId: values.linkedReservationId,
                        segmentId: values.segmentId || null,
                        unitAmount: {
                            value: values.value,
                            currency: values.currency,
                            ...(hasEnterpriseNetPricing ? { netValue: values.value } : { grossValue: values.value }),
                            taxValues: calculateTaxValues(values),
                        },
                        startUtc: values.startUtc,
                        note: values.note,
                        productOrders: getFinalProductOrders(values.productOrders || null, hasOverridablePrice),
                    },
                ],
            });
        },
    });

    return {
        isLoading: isPending,
        renderFormContent,
        submit,
        watch,
        reset,
        hasDirtyFields,
        validate,
    };
};

const getFinalProductOrders = (productOrders: ProductOrderFormValues[] | null, hasOverridablePrice: boolean) => {
    if (!productOrders?.length) {
        return [];
    }

    if (hasOverridablePrice) {
        return productOrders;
    }

    return productOrders.map((po) => ({ ...po, unitCost: null }));
};
